// CO Log rows, shared by the CO Log tab and the Overview page's download.
//
// The CO Log is stored one row per field: Forecast_Tab 'CO Log' (older rows
// spell it 'CO LOG'), Forecast_Row_ID identifying the CO line, and the value
// carried on Value_Amount under the field's Forecast_Field_Name. Folding those
// back into one object per line is the only way to read a CO log, so both the
// tab and Overview do it here rather than each keeping its own copy — the two
// drifting is how Overview would quietly report a different CO log than the
// tab it links to.

import { fNum, pNum } from '../db/company';

/**
 * Fold the per-field CO Log rows into one object per CO line.
 * @param {Array} jobUserData - raw forecast user-data rows for the job.
 * @param {{ job?: string|number, version?: string }} [scope] - when given,
 *   only rows on that job/version are folded. The CO Log tab loads one
 *   version at a time so it passes nothing; Overview holds every version's
 *   rows at once and has to narrow.
 * @returns {Array} one row per CO line, sorted by CO number.
 */
export const buildCOLogRows = (jobUserData, scope = {}) => {
    if (!Array.isArray(jobUserData) || jobUserData.length === 0) return [];
    const { job, version } = scope;
    const byRow = {};
    jobUserData.forEach((d) => {
        if (d?.Forecast_Tab !== 'CO Log' && d?.Forecast_Tab !== 'CO LOG') return;
        // Loose compares: BU_ID and Version_GUID come back as numbers on some
        // routes and strings on others.
        if (job != null && d.BU_ID != job) return;
        if (version != null && version !== '' && d.Version_GUID != version) return;
        const id = d?.Forecast_Row_ID;
        if (byRow[id]) {
            byRow[id] = {
                ...byRow[id],
                ...d,
                [d.Forecast_Field_Name]: d.Value_Amount,
                // A blank Status on a later field row must not wipe the one
                // the line already carries.
                Status: d.Status ? d.Status : byRow[id]?.Status,
            };
        } else {
            byRow[id] = { ...d, [d.Forecast_Field_Name]: d.Value_Amount, id };
        }
        if (d?.Forecast_Field_Name === 'Quantity') byRow[id].budgetQty = d.Value_Amount;
    });
    return sortCOLogRows(Object.values(byRow));
};

/** Sort by CO number, numerically where it parses, blanks last. */
export const sortCOLogRows = (rows) => [...rows].sort((a, b) => {
    const aV = a?.Row_Label || '';
    const bV = b?.Row_Label || '';
    if (!aV) return 1;
    if (!bV) return -1;
    const aN = parseFloat(aV);
    const bN = parseFloat(bV);
    if (!isNaN(aN) && !isNaN(bN)) return aN - bN;
    return String(aV).localeCompare(String(bV));
});

// Cost-type columns, in CO Log order. Kept as one list so the header row and
// the value row can never fall out of step.
const COST_TYPE_COLS = [
    '51100', '51200', '51300', '51400', '51500', '51600', '51700', '51800',
    '52100', '52200', '52300', '52400',
    '53100', '53200', '53300', '53400',
    '54100', '54200', '54300',
    '55100', '55200', '55300', '55400',
    '56100', '56200', '56300', '56400', '56500', '56600', '56700', '56800',
    '57100', '57200', '57300', '57900',
];

export const CO_LOG_HEADERS = [
    'In JDE', 'CO No', 'Phase', 'Item Description', 'Status', 'Unit Price', 'T&M',
    'Change Orders', 'Reduction of Scope', 'Total Revenue',
    'Budget Qty', 'Total Labor Hours', 'Total Equipment Hours',
    ...COST_TYPE_COLS,
    'Overhead', 'Total Costs', 'Gross Margin', 'GP %', 'Additional Notes',
];

/**
 * Turn folded CO Log rows into an array-of-arrays for a worksheet, headers
 * first. Field names are read both camelCase (what the tab's editors write)
 * and in their stored column spelling (what an older row carries).
 * @param {Array} logs
 * @returns {Array[]}
 */
export const coLogToSheetRows = (logs) => {
    const rows = (logs || []).map((row) => [
        row.inJDE || '',
        row.coNo || row.Row_Label || '',
        row.phase || row.Cost_Code || '',
        row.itemDescription || row.Description || '',
        row.status || row.Status || '',
        row.unitPrice || '',
        row.tAndM || '',
        row.changeOrders || '',
        row.reductionOfScope || '',
        row.totalRevenue || '',
        row.budgetQty || row.Quantity || '',
        row.totalLaborHours || row.Hours || '',
        row.totalEquipmentHours || '',
        ...COST_TYPE_COLS.map((ct) => row[ct] || ''),
        row.overhead || '',
        row.totalCosts || '',
        row.grossProfit || '',
        row.gpPercent || '',
        row.additionalNotes || row.Notes || '',
    ]);
    return [CO_LOG_HEADERS, ...rows];
};

/**
 * Revenue / cost / margin totals across the folded rows, for the sheet's
 * footer. Only the money and hours columns roll up; the rest are per-line.
 * @param {Array} logs
 * @returns {Array} one array-of-cells row aligned to CO_LOG_HEADERS.
 */
export const coLogTotalsRow = (logs) => {
    const add = (field) => (logs || []).reduce((s, r) => s + pNum(r[field]), 0);
    const revenue = add('totalRevenue');
    const costs = add('totalCosts');
    const gp = revenue - costs;
    const out = new Array(CO_LOG_HEADERS.length).fill('');
    out[1] = 'TOTAL';
    out[5] = fNum(add('unitPrice'));
    out[6] = fNum(add('tAndM'));
    out[7] = fNum(add('changeOrders'));
    out[8] = fNum(add('reductionOfScope'));
    out[9] = fNum(revenue);
    out[11] = fNum(add('totalLaborHours'));
    out[12] = fNum(add('totalEquipmentHours'));
    out[CO_LOG_HEADERS.indexOf('Overhead')] = fNum(add('overhead'));
    out[CO_LOG_HEADERS.indexOf('Total Costs')] = fNum(costs);
    out[CO_LOG_HEADERS.indexOf('Gross Margin')] = fNum(gp);
    out[CO_LOG_HEADERS.indexOf('GP %')] = revenue === 0 ? '-' : `${(gp / revenue * 100).toFixed()}%`;
    return out;
};
